import { useState } from 'react';
import { useI18n, formatDay } from '../../i18n';
import { countLabel } from '../../i18n/labels';
import { useToast } from '../../hooks/useToast';
import { useBusy } from '../../hooks/useBusy';
import { useLoad } from '../../hooks/useLoad';
import ModalBackdrop from '../shared/ModalBackdrop';
import ModalHeader from '../shared/ModalHeader';
import ModalFooter from '../shared/ModalFooter';
import Field from '../shared/Field';
import ConfirmModal, { SECOND_QUESTION_ARM_MS } from '../shared/ConfirmModal';
import { TrashIcon } from '../shared/Icons';
import { SectionTitle } from './QualityTables';
import {
  addOfficialAccreditation, addOfficialTeamPeriod, getTeams, removeOfficialAccreditation,
  removeOfficialTeamPeriod, setOfficialAccreditationEnd,
} from '../../api/client';
import type { OfficialProfile as Profile, SeatStatus, Team } from '../../types';

/** #641, #642 — the profile of an official: the team history, the IFMA
 *  accreditation and the novice status of each seat today. The server sends
 *  the profile only to canSeeAllOfficialStats (admin, tournament_manager,
 *  jury), and only they can change it. A non-admin cannot change the own
 *  profile (403 OFFICIAL_PROFILE_SELF_EDIT): then `canEdit` is false, and
 *  the page shows no edit control. Each write answers with the new profile.
 *  The rules are in backend lib/officialProfile.ts. */
export default function OfficialProfile({ userId, profile, canEdit, onChange }: {
  userId: string; profile: Profile; canEdit: boolean; onChange: (p: Profile) => void;
}) {
  const { t, lang } = useI18n();
  const { showError } = useToast();
  const busy = useBusy();
  const [modal, setModal] = useState<null | 'team' | 'accreditation' | { end: string }>(null);
  /** A delete asks two times (a hard delete). */
  const [remove, setRemove] = useState<null | { kind: 'team' | 'accreditation'; id: string; step: 1 | 2 }>(null);
  const o = t.officials;
  const day = (d: string) => formatDay(d, 'd MMM yyyy');

  /** The answer of a write: the new profile, or the error. Each caller runs it
   *  inside run() of useBusy, so a second tap sends no copy (#605). */
  const apply = async (call: Promise<{ data: { profile: Profile } }>) => {
    try {
      const res = await call;
      onChange(res.data.profile);
      setModal(null);
      setRemove(null);
    } catch (err) { showError(err); }
  };

  const seatLine = (label: string, s: SeatStatus | null, testId: string, ring = false) => {
    if (!s) return null;
    const state = profile.accredited ? o.experiencedIfma : s.novice ? o.novice : o.experienced;
    const parts = [
      o.seatBouts.replace('{bouts}', countLabel(lang, s.bouts, { one: t.common.boutsOne, few: t.common.boutsFew, other: t.common.boutsOther })),
      ...(ring ? [o.seatHard.replace('{n}', String(s.hard_bouts ?? 0))] : []),
      s.last_day ? o.seatLast.replace('{date}', day(s.last_day)) : o.seatNoSeat,
    ];
    return (
      <div data-testid={testId} data-novice={String(s.novice)} className="flex flex-col sm:flex-row sm:items-baseline gap-x-2">
        <span className="text-xs text-gray-500 sm:w-28 shrink-0">{label}</span>
        <span className="text-sm">
          <span className={`font-semibold ${s.novice ? 'text-sky-700' : 'text-gray-800'}`}>{state}</span>
          <span className="text-xs text-gray-500"> · {parts.join(' · ')}</span>
        </span>
      </div>
    );
  };

  const current = profile.accreditations.find(a => a.valid_from <= profile.day && (!a.valid_until || a.valid_until >= profile.day));

  return (
    <div className="card p-4 space-y-4" data-testid="official-profile">
      <SectionTitle title={o.profileTitle} hint={o.profileHint} />

      {/* The team and its history. */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="min-w-0">
            <span className="text-xs text-gray-500">{o.teamLabel}: </span>
            <span data-testid="official-profile-team" data-team-id={profile.team?.id ?? ''} className="text-sm font-semibold break-words">
              {profile.team?.name ?? o.noTeam}
            </span>
          </div>
          {canEdit && (
            <button type="button" className="btn-outline btn-sm" data-testid="official-team-change" onClick={() => setModal('team')}>
              {o.changeTeam}
            </button>
          )}
        </div>
        {profile.teams.length > 0 && (
          <ul className="text-xs text-gray-600 space-y-1" data-testid="official-team-history">
            {profile.teams.map(p => (
              <li key={p.id} className="flex items-center justify-between gap-2" data-testid="official-team-period" data-period-id={p.id} data-team-id={p.team_id ?? ''}>
                <span className="min-w-0 break-words">{o.fromDate.replace('{date}', day(p.valid_from))} · {p.team_name ?? o.noTeam}</span>
                {canEdit && (
                  <button type="button" className="btn-icon-red shrink-0" title={o.removePeriod} aria-label={o.removePeriod} data-testid="official-team-period-remove"
                    onClick={() => setRemove({ kind: 'team', id: p.id, step: 1 })}>
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* The IFMA accreditation. */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="min-w-0">
            <span className="text-xs text-gray-500">{o.accreditationLabel}: </span>
            <span data-testid="official-profile-accredited" data-accredited={String(profile.accredited)} className="text-sm font-semibold">
              {profile.accredited ? o.accredited : o.notAccredited}
            </span>
          </div>
          {canEdit && (
            <button type="button" className="btn-outline btn-sm" data-testid="official-accreditation-add" onClick={() => setModal('accreditation')}>
              {o.addAccreditation}
            </button>
          )}
        </div>
        {profile.accreditations.length > 0 && (
          <ul className="text-xs text-gray-600 space-y-1" data-testid="official-accreditations">
            {profile.accreditations.map(a => (
              <li key={a.id} className="flex items-center justify-between gap-2" data-testid="official-accreditation" data-accreditation-id={a.id}>
                <span className="min-w-0">{day(a.valid_from)} – {a.valid_until ? day(a.valid_until) : o.noEnd}</span>
                <span className="flex items-center gap-1 shrink-0">
                  {canEdit && a === current && !a.valid_until && (
                    <button type="button" className="btn-outline btn-sm" data-testid="official-accreditation-end"
                      onClick={() => setModal({ end: a.id })}>{o.endAccreditation}</button>
                  )}
                  {canEdit && (
                    <button type="button" className="btn-icon-red" title={o.removeAccreditation} aria-label={o.removeAccreditation} data-testid="official-accreditation-remove"
                      onClick={() => setRemove({ kind: 'accreditation', id: a.id, step: 1 })}>
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* The novice status of each seat, today. */}
      <div className="space-y-1.5">
        {seatLine(o.seatSideJudge, profile.side_judge, 'official-status-side-judge')}
        {seatLine(o.seatRingReferee, profile.ring_referee, 'official-status-ring-referee', true)}
        <p className="text-xs text-gray-400">{o.noviceRule}</p>
      </div>

      {modal === 'team' && (
        <TeamPeriodModal busy={busy.busy} day={profile.day} onCancel={() => setModal(null)}
          onSave={(teamId, from) => busy.run(() => apply(addOfficialTeamPeriod(userId, { team_id: teamId, valid_from: from })))} />
      )}
      {modal === 'accreditation' && (
        <DatesModal title={o.addAccreditation} withEnd busy={busy.busy} day={profile.day} onCancel={() => setModal(null)}
          onSave={(from, until) => busy.run(() => apply(addOfficialAccreditation(userId, { valid_from: from, valid_until: until })))} />
      )}
      {modal && typeof modal === 'object' && (
        <DatesModal title={o.endAccreditationTitle} endOnly busy={busy.busy} day={profile.day} onCancel={() => setModal(null)}
          onSave={(until) => busy.run(() => apply(setOfficialAccreditationEnd(userId, modal.end, until)))} />
      )}
      {remove && (
        <ConfirmModal destructive
          key={remove.step}
          armMs={remove.step === 2 ? SECOND_QUESTION_ARM_MS : 0}
          title={remove.kind === 'team' ? o.removePeriodTitle : o.removeAccreditationTitle}
          message={remove.step === 1 ? o.removeQuestion : o.removeSure}
          confirmLabel={t.common.delete}
          busy={busy.busy}
          testId={`official-remove-confirm-${remove.step}`}
          onCancel={() => setRemove(null)}
          onConfirm={() => remove.step === 1
            ? setRemove({ ...remove, step: 2 })
            : remove.kind === 'team'
              ? busy.run(() => apply(removeOfficialTeamPeriod(userId, remove.id)))
              : busy.run(() => apply(removeOfficialAccreditation(userId, remove.id)))}
        />
      )}
    </div>
  );
}

/** The new team period: a team, or no team, from a date. `day` is today of
 *  the server (the profile), so the browser zone cannot move the default. */
function TeamPeriodModal({ busy, day, onSave, onCancel }: {
  busy: boolean; day: string; onSave: (teamId: string | null, from: string) => void; onCancel: () => void;
}) {
  const { t } = useI18n();
  const { showError } = useToast();
  const o = t.officials;
  // A failed read of the teams keeps Save off: the select would offer "No team" only.
  const teamsLoad = useLoad(
    () => getTeams().then(r => ([...r.data] as Team[]).sort((a, b) => a.name.localeCompare(b.name))),
    [],
    { context: 'OfficialProfile:teams', initial: [] as Team[], expected: () => true, onError: err => showError(err) },
  );
  const teams = teamsLoad.data;
  const teamsLoaded = !teamsLoad.loading && !teamsLoad.error;
  const [teamId, setTeamId] = useState('');
  const [from, setFrom] = useState(day);
  return (
    <ModalBackdrop onClose={onCancel} maxWidth="sm" ariaLabel={o.teamModalTitle}>
      <ModalHeader title={o.teamModalTitle} />
      <div className="space-y-3">
        <Field label={o.teamLabel}>
          <select id="official-team-select" data-testid="official-team-select" className="input text-sm" value={teamId}
            onChange={e => setTeamId(e.target.value)} data-autofocus>
            <option value="">{o.noTeam}</option>
            {teams.map(tm => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
          </select>
        </Field>
        <Field label={o.fromLabel} help={o.teamPeriodHint}>
          <input id="official-team-from" data-testid="official-team-from" type="date" className="input text-sm" value={from}
            onChange={e => setFrom(e.target.value)} />
        </Field>
      </div>
      <ModalFooter onSave={() => onSave(teamId || null, from)} busy={busy} saveDisabled={!from || !teamsLoaded}
        onCancel={onCancel} saveTestId="official-team-save" />
    </ModalBackdrop>
  );
}

/** The dates of an accreditation: the start and an optional end, or the end only. */
function DatesModal({ title, withEnd = false, endOnly = false, busy, day, onSave, onCancel }: {
  title: string; withEnd?: boolean; endOnly?: boolean; busy: boolean; day: string;
  onSave: (a: string, b: string | null) => void; onCancel: () => void;
}) {
  const { t } = useI18n();
  const o = t.officials;
  const [from, setFrom] = useState(day);
  const [until, setUntil] = useState(endOnly ? day : '');
  return (
    <ModalBackdrop onClose={onCancel} maxWidth="sm" ariaLabel={title}>
      <ModalHeader title={title} />
      <div className="space-y-3">
        {!endOnly && (
          <Field label={o.fromLabel}>
            <input id="official-dates-from" data-testid="official-dates-from" type="date" className="input text-sm" value={from}
              onChange={e => setFrom(e.target.value)} data-autofocus />
          </Field>
        )}
        {(withEnd || endOnly) && (
          <Field label={endOnly ? o.untilLabel : o.untilOptional}>
            <input id="official-dates-until" data-testid="official-dates-until" type="date" className="input text-sm" value={until}
              onChange={e => setUntil(e.target.value)} />
          </Field>
        )}
      </div>
      <ModalFooter
        onSave={() => (endOnly ? onSave(until, null) : onSave(from, until || null))}
        busy={busy} saveDisabled={endOnly ? !until : !from}
        onCancel={onCancel} saveTestId="official-dates-save" />
    </ModalBackdrop>
  );
}
